import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { usePreference } from '@/hooks/useUserPreferences';
import { useDeskHabit } from '@/hooks/useDeskHabit';
import { useFeatureRegistration, type FeatureMeta } from '@/contexts/FeatureRegistryContext';
import { trackActivity } from '@/lib/activityTracking';
import { withCue } from '@/lib/spotlightCue';
import { stepProgress, habitFraction, pickHabitRep, repLine, repButton, HABIT_DONE_LINE } from '@/lib/deskHabit';
import NextMoveStrip from '@/components/desk/NextMoveStrip';
import { useNextMove } from '@/hooks/useNextMove';
import { useRibbonAttention } from '@/hooks/useRibbonAttention';
import { cn } from '@/lib/utils';
import { shouldSweep, recordSweep, readSweepLog, writeSweepLog } from '@/lib/ribbonAttention';

export const FEATURE_META: FeatureMeta = {
  id: 'desk-setup',
  label: 'Desk Setup',
  description:
    'A slim ribbon under the header on every page that shows your setup progress and the one step that fits the page you are on; tapping it opens Your Desk. Once setup is done, Your Next Move™ takes over.',
  triggerType: 'panel',
  personas: ['seller', 'buyer_seller', 'buyer'],
};

/** Ring showing overall progress. */
function Arc({ value }: { value: number }) {
  const r = 9, c = 2 * Math.PI * r;
  return (
    <svg viewBox="0 0 24 24" className="h-6 w-6 shrink-0 -rotate-90" aria-hidden>
      <circle cx="12" cy="12" r={r} fill="none" className="stroke-border" strokeWidth="3" />
      <circle cx="12" cy="12" r={r} fill="none" className="stroke-primary transition-all" strokeWidth="3"
        strokeDasharray={c} strokeDashoffset={c * (1 - value)} strokeLinecap="round" />
    </svg>
  );
}

export default function DeskRibbon() {
  useFeatureRegistration(FEATURE_META);
  const { pathname } = useLocation();
  const { progress, reps, persona } = useDeskHabit();
  const [collapsed, setCollapsed] = usePreference<boolean>('desk-ribbon-collapsed', false);
  const shownFor = useRef<string | null>(null);

  const rep = pickHabitRep(pathname, reps);
  const visible = !!progress && progress.ribbonEnabled && !progress.graduated && reps.length > 0;
  // One surface for life: once Desk Setup is finished, the same ribbon carries Your Next Move™.
  const showNextMove = !!progress && progress.ribbonEnabled && progress.graduated;

  const { move } = useNextMove(showNextMove);
  // Caught up: the ribbon steps aside so the screen is all work.
  const quiet = showNextMove && (!move || move.kind === 'caught_up');
  const attentionKey = showNextMove ? (move && move.kind !== 'caught_up' ? move.key : null) : visible ? rep?.key ?? null : null;
  const attention = useRibbonAttention();
  const [sweeping, setSweeping] = useState(false);
  useEffect(() => {
    if (!attentionKey || pathname === '/desk') return;
    const log = readSweepLog(), now = Date.now();
    if (!shouldSweep(log, attentionKey, now, attention)) return;
    writeSweepLog(recordSweep(log, attentionKey, now));
    setSweeping(true);
    const t = setTimeout(() => setSweeping(false), 5400);
    return () => clearTimeout(t);
  }, [attentionKey, pathname, attention.maxPerMove, attention.restMs]);

  useEffect(() => {
    if (!visible || shownFor.current === pathname) return;
    shownFor.current = pathname;
    trackActivity('desk_ribbon_shown', { route: pathname, rep_key: rep?.key ?? null, version: progress!.version });
  }, [visible, pathname, rep?.key, progress]);

  if (showNextMove) {
    // Your Desk shows the same move as its own headline — never twice on one screen.
    if (pathname === '/desk' || quiet) return null;
    // Phones: a slim capsule (one line + action) so the page's own work starts higher.
    return (
      <div className={cn('desk-paper rounded-full py-1 pl-3 pr-1 sm:rounded-xl sm:px-4 sm:py-2', sweeping && 'ribbon-sweep')}>
        <NextMoveStrip surface="ribbon" pulse={sweeping && !!move && move.kind === 'move' && attention.urgentKeys.includes(move.key)} />
      </div>
    );
  }
  if (!visible) return null;
  // Your Desk shows the full Desk Setup list itself.
  if (pathname === '/desk') return null;
  const pct = habitFraction(reps);
  const done = reps.filter((r) => r.done).length;

  const go = (key: string) => {
    trackActivity('desk_suggestion_clicked', { rep_key: key, route: pathname, surface: 'ribbon' });
  };

  // One job: the step and its action. The full list lives on Your Desk; the assistant lives in the header.
  return (
    <div className={cn('flex items-center gap-3 rounded-xl desk-paper px-3 py-2 sm:px-4', sweeping && 'ribbon-sweep')}>
      <Link to="/desk" onClick={() => trackActivity('desk_ribbon_opened', { route: pathname })} className="flex min-w-0 flex-1 items-center gap-3 text-left" aria-label={`Desk Setup, ${done} of ${reps.length} done — open Your Desk`}>
        <Arc value={pct} />
        <span className="min-w-0">
          <span className="block text-xs font-medium uppercase tracking-wide text-muted-foreground">Desk Setup{rep ? ` · ${stepProgress(rep)}` : ''}</span>
          {!collapsed && <span className="block truncate text-sm">{rep ? repLine(rep, persona) : HABIT_DONE_LINE}</span>}
        </span>
      </Link>
      {!collapsed && rep && (
        <Button asChild size="sm" variant="guide" className="shrink-0">
          <Link to={rep.cue ? withCue(rep.route, rep.cue) : rep.route} onClick={() => go(rep.key)}>
            <span className="hidden min-[420px]:inline">{repButton(rep, persona) ?? 'Open'}</span><ArrowRight className="h-4 w-4 min-[420px]:ml-1" aria-hidden /><span className="sr-only min-[420px]:hidden">{repButton(rep, persona) ?? 'Open'}</span>
          </Link>
        </Button>
      )}
      <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={() => setCollapsed(!collapsed)} aria-label={collapsed ? 'Expand Desk Setup' : 'Collapse Desk Setup'}>
        {collapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
      </Button>
    </div>
  );
}
